import React, { useEffect, useState, Fragment, useRef, useMemo } from "react";
import { Card, CardBody, Table, Button } from "reactstrap";
import { useParams, useNavigate } from "react-router-dom";
import { usePharmacy } from "../../_helper/PharmacyProvider";
import { viewLedgerPayment } from "./PatientLedgerService";
import { useLoader } from "../../_helper/LoaderContext";
import { useTranslation } from "react-i18next";
import { FaLocationDot } from "react-icons/fa6";
import { TfiEmail } from "react-icons/tfi";
import { FaRegCreditCard, FaMoneyBillWave } from "react-icons/fa";
import { IoCashOutline } from "react-icons/io5";

import { FiPhoneCall } from "react-icons/fi";
import { ArrowLeft } from "react-feather";
import moment from "moment";
import { toast } from "react-toastify";
import { Modal } from "react-bootstrap";
import { ToolTip } from "../../AbstractElements";
import S3ImageLoader from "../../S3ImageLoader";
import errorProfile from "../../assets/images/blank_profile.png";
import { useCurrency } from "../../_helper/CurrencyContext";
import { canShowPatientAccounts } from "../../utils/pharmacyConnection";
import { applyDiscount, toDecimal, toMoneyNumber } from "../../utils/financeEngine";
import {
  buildPatientLedgerReceiptHtml,
  openTransactionPrintWindow,
} from "../../utils/transactionPrint";
import InvoicePreviewModal from "../PatientAccounts/components/InvoicePreviewModal";
import InvoiceModal from "../PharmacySales/NewSales/components/InvoiceModal";
import { buildInvoiceFromSale } from "../PharmacySales/utils/buildInvoiceFromSale";
import { shouldShowSaleTaxUi } from "../PharmacyManageTax/taxConfigUtils";

const getPaymentInvoiceUrl = (payment) =>
  payment?.invoiceUrl || payment?.sales?.invoiceUrl || "";

const getPaymentInvoiceNo = (payment) =>
  payment?.salesNumber ||
  payment?.billing?.appointment_invoice?.invoice_number ||
  payment?.sales?.billing?.appointment_invoice?.invoice_number ||
  "";

const canShowPosReceipt = (payment) =>
  payment?.salesType === "refund" && Boolean(payment?.sales?.items?.length);

const buildPosReceiptData = (payment, getCurrencySymbol) => {
  if (!canShowPosReceipt(payment)) return null;

  const sale = payment.sales;
  return buildInvoiceFromSale(
    {
      ...sale,
      patientName: payment.patientName || sale.patientName,
      salesNumber: sale.salesNumber || payment.receiptNumber || payment.salesNumber,
      netTotal: sale.netTotal ?? payment.amount,
      totalPaid: sale.totalPaid ?? payment.amount,
      paymentType: payment.paymentType || sale.paymentType,
      createdAt: payment.createdAt || sale.createdAt,
      salesType: sale.salesType || payment.salesType,
    },
    getCurrencySymbol
  );
};

const ViewPatientLedger = () => {
  const { Id } = useParams();
  const { pharmacyPOS } = usePharmacy();
  const { setLoading } = useLoader();
  const { t } = useTranslation();
  const { getCurrencySymbol, convertCurrency, currency } = useCurrency();
  const navigate = useNavigate();

  const showInvoicePreview = canShowPatientAccounts(pharmacyPOS);

  const canViewPaymentInvoice = (payment) =>
    showInvoicePreview &&
    payment?.customerType === "hospital_patient" &&
    Boolean(getPaymentInvoiceUrl(payment));

  const [ledgerData, setLedgerData] = useState({
    payments: [],
    totalDueAmount: {},
    totalPaidAmount: 0,
    netTotal: 0,
    paymentCount: 0,
    totalRefund: 0,
    totalReturn: 0,
  });
  const [patientInfo, setPatientInfo] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedPaymentDetails, setSelectedPaymentDetails] = useState(null);
  const [viewTooltipOpen, setViewTooltipOpen] = useState({});
  const [invoiceTooltipOpen, setInvoiceTooltipOpen] = useState({});
  const [invoicePreviewOpen, setInvoicePreviewOpen] = useState(false);
  const [previewInvoiceUrl, setPreviewInvoiceUrl] = useState("");
  const [previewInvoiceNo, setPreviewInvoiceNo] = useState("");
  const [showPosInvoice, setShowPosInvoice] = useState(false);
  const [posInvoiceData, setPosInvoiceData] = useState(null);
  const [receiptTooltipOpen, setReceiptTooltipOpen] = useState({});
  const eyeButtonRefs = useRef({});

  const calculatedSalesFooter = useMemo(() => {
    if (!selectedPaymentDetails?.sales?.items) return null;

    const footer = selectedPaymentDetails.sales.items.reduce(
      (acc, item) => {
        const subTotal = Number(item.subTotal || item.totalPrice || 0); // line total (tax-inclusive)
        const taxPercentage =
          Number(item.taxPercentage) ||
          Number(selectedPaymentDetails.sales.taxPercentage) ||
          0;

        // Taxable before any bill-level discount (derived from subTotal)
        const taxableAmount =
          taxPercentage > 0 ? subTotal / (1 + taxPercentage / 100) : subTotal;

        // Use stored tax splits from API (already reflect discount logic for new sales)
        const lineCGST = Number(item.CGST || 0);
        const lineSGST = Number(item.SGST || 0);
        const lineIGST = Number(item.IGST || 0);
        const lineTax =
          lineCGST + lineSGST + lineIGST || Number(item.tax || 0);

        acc.totalTax += lineTax;
        acc.CGST += lineCGST;
        acc.SGST += lineSGST;
        acc.IGST += lineIGST;
        acc.subtotal += taxableAmount; // Total Taxable Value (before discount)
        acc.totalPrice += subTotal;

        return acc;
      },
      { totalTax: 0, CGST: 0, SGST: 0, IGST: 0, subtotal: 0, totalPrice: 0 }
    );

    const discountAmt =
      selectedPaymentDetails.sales.discountType === "percentage" ||
      selectedPaymentDetails.sales.discountType === "Percentage"
        ? toMoneyNumber(
            applyDiscount(
              toDecimal(footer.subtotal),
              "Percentage",
              selectedPaymentDetails.sales.discount || 0
            )
          )
        : toMoneyNumber(selectedPaymentDetails.sales.discount || 0);

    return { ...footer, discountAmt };
  }, [selectedPaymentDetails]);

  const showSaleTax = useMemo(() => {
    const sales = selectedPaymentDetails?.sales;
    if (!sales) return false;
    return shouldShowSaleTaxUi(pharmacyPOS, {
      saleTax: sales.tax,
      items: sales.items || [],
    });
  }, [pharmacyPOS, selectedPaymentDetails]);

  const taxSlabs = useMemo(() => {
    if (!selectedPaymentDetails?.sales?.items) return [];
    return [
      ...new Set(
        selectedPaymentDetails.sales.items.map(
          item => Number(item.taxPercentage || 0)
        )
      ),
    ];
  }, [selectedPaymentDetails]);

  const hasMixedTaxSlabs = taxSlabs.length > 1;

  const taxBreakdownBySlab = useMemo(() => {
    if (!selectedPaymentDetails?.sales?.items) return [];

    const map = {};
    selectedPaymentDetails.sales.items.forEach(item => {
      const slab = item.taxPercentage;
      if (!map[slab]) {
        map[slab] = { CGST: 0, SGST: 0, IGST: 0 };
      }
      map[slab].CGST += Number(item.CGST || 0);
      map[slab].SGST += Number(item.SGST || 0);
      map[slab].IGST += Number(item.IGST || 0);
    });

    return Object.entries(map);
  }, [selectedPaymentDetails]);

  // Get available currencies from ledger data
  const availableCurrencies = useMemo(() => {
    if (ledgerData.payments.length === 0) return ['USD']; // Default fallback

    const currencies = new Set();
    ledgerData.payments.forEach(payment => {
      if (payment.currency) currencies.add(payment.currency);
      if (payment.balances) {
        Object.keys(payment.balances).forEach(currency => currencies.add(currency));
      }
    });

    // Get the base currency (first currency from balances or payments)
    const baseCurrency = ledgerData.payments[0]?.baseCurrency ||
      Object.keys(ledgerData.payments[0]?.balances || {})[0] ||
      'INR';

    // Sort currencies with base currency first
    const sortedCurrencies = Array.from(currencies);
    if (sortedCurrencies.includes(baseCurrency)) {
      const baseIndex = sortedCurrencies.indexOf(baseCurrency);
      sortedCurrencies.splice(baseIndex, 1);
      sortedCurrencies.unshift(baseCurrency);
    }

    return sortedCurrencies.length > 0 ? sortedCurrencies : ['USD'];
  }, [ledgerData.payments]);

  // Get base currency (first currency)
  const baseCurrency = availableCurrencies[0];

  const toggleViewTooltip = (id) => {
    setViewTooltipOpen((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  const toggleInvoiceTooltip = (id) => {
    setInvoiceTooltipOpen((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  const openInvoicePreview = (payment) => {
    setPreviewInvoiceUrl(getPaymentInvoiceUrl(payment));
    setPreviewInvoiceNo(getPaymentInvoiceNo(payment));
    setInvoicePreviewOpen(true);
  };

  const closeInvoicePreview = () => {
    setInvoicePreviewOpen(false);
    setPreviewInvoiceUrl("");
    setPreviewInvoiceNo("");
  };

  const toggleReceiptTooltip = (id) => {
    setReceiptTooltipOpen((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  const openPosReceipt = (payment) => {
    const invoiceData = buildPosReceiptData(payment, getCurrencySymbol);
    if (!invoiceData) return;
    setPosInvoiceData(invoiceData);
    setShowPosInvoice(true);
  };

  const closePosReceipt = () => {
    setShowPosInvoice(false);
    setPosInvoiceData(null);
  };

  const printPosInvoice = () => {
    const invoiceContent = document?.getElementById("invoice-content");
    if (!invoiceContent) return;
    const printWindow = window.open("", "", "width=400,height=600");
    if (!printWindow) return;
    printWindow.document.write(`
      <html>
        <head>
          <title>Invoice</title>
          <style>
            @page { size: 80mm auto; margin: 5mm; }
            body { font-family: Arial, sans-serif; font-size: 12px; width: 80mm; margin: 0 auto; }
            .text-center { text-align: center; }
            .text-end { text-align: right; }
            .bold { font-weight: bold; }
            .dotted { border-bottom: 1px dotted #000; margin: 4px 0; }
            table { width: 100%; border-collapse: collapse; font-size: 12px; }
            td { padding: 2px 0; vertical-align: top; }
            .section { margin-top: 6px; margin-bottom: 6px; }
            .thankyou { margin-top: 12px; font-size: 12px; text-align: center; }
          </style>
        </head>
        <body>${invoiceContent.innerHTML}</body>
      </html>
    `);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
      printWindow.close();
    }, 400);
  };

  useEffect(() => {
    if (Id) {
      getData();
    }
  }, []);

  const getData = async () => {
    try {
      setLoading(true);
      const data = {
        clinicId: pharmacyPOS.hospital_id,
        patientId: Id,
      };
      const res = await viewLedgerPayment(data);

      if (res?.data?.status === "success") {
        setLedgerData(res.data.data);
        const { patient } = res.data.data;
        setPatientInfo(patient);
      }
    } catch (error) {
      console.error("Error fetching ledger data:", error);
      toast.warning(t("Failed to fetch patient ledger data"));
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    const day = date.getDate().toString().padStart(2, "0");
    const month = date.toLocaleString(undefined, { month: "short" });
    const year = date.getFullYear();
    return `${day} ${month} ${year}`;
  };

  const getTransactionTypeLabel = (salesType) => {
    return t(`${salesType.charAt(0).toUpperCase() + salesType.slice(1)}`);
  };

  const getStatusBadge = (payment) => {
    if (payment.isSettled) {
      return <span className="badge bg-success">{t("Settled")}</span>;
    }
    if (payment.isPaid) {
      return <span className="badge bg-primary">{t("Paid")}</span>;
    }
    if (payment.isPartialPaid) {
      return <span className="badge bg-warning">{t("Partial")}</span>;
    }
    // Check if there's any balance due
    const hasBalanceDue = payment.balances && Object.values(payment.balances).some(balance => balance > 0);
    if (hasBalanceDue) {
      return <span className="badge bg-danger">{t("Pending")}</span>;
    }
    if (payment.salesType === "return") {
      return <span className="badge bg-danger">{t("Return")}</span>;
    }
    return <span className="badge bg-secondary">{t("Unknown")}</span>;
  };

  const calculateAge = (birthDate) => {
    if (!birthDate) return "N/A";
    const birthDateMoment = moment(birthDate);
    const currentDate = moment();
    const age = currentDate.diff(birthDateMoment, "years");
    return age;
  };

  const openDetailsModal = (payment) => {
    setSelectedPaymentDetails(payment);
    setShowDetailsModal(true);
  };

  const handlePrint = () => {
    if (!selectedPaymentDetails) return;
    const html = buildPatientLedgerReceiptHtml(selectedPaymentDetails, {
      pharmacyName:
        pharmacyPOS?.facility_name || pharmacyPOS?.hospital?.name || "",
      t,
      getCurrencySymbol,
      formatDate,
      calculatedSalesFooter,
      getTransactionTypeLabel,
      hasAnyBalance,
    });
    openTransactionPrintWindow(
      `Receipt_${selectedPaymentDetails.receiptNumber || "transaction"}`,
      html
    );
  };

  const formatAmount = (num) => {
    const val = typeof num === "number" ? num : Number(num);
    if (isNaN(val) || val === 0) return "0.00";
    if (val >= 1_000_000_000) return (val / 1_000_000_000).toFixed(1) + "B";
    if (val >= 1_000_000) return (val / 1_000_000).toFixed(1) + "M";
    if (val >= 1_000) return (val / 1_000).toFixed(1) + "K";
    return val.toFixed(2);
  };

  // Helper function to get amount in display currency
  const getDisplayAmount = (payment, isCredit = false) => {
    if (!payment) return { primary: "0.00", secondary: [] };

    const primaryCurrency = payment.currency || baseCurrency;
    const primarySymbol = getCurrencySymbol(primaryCurrency);

    if (isCredit) {
      // For credit transactions (payments)
      const primaryAmount = payment.amount?.toFixed(2) || "0.00";

      // Generate secondary amounts for all other currencies
      const secondaryAmounts = availableCurrencies
        .filter(currency => currency !== primaryCurrency)
        .map(currency => {
          const rate = payment.currencyRates?.[currency] || 1;
          const convertedAmount = primaryCurrency === baseCurrency
            ? (payment.amount / rate)?.toFixed(2)
            : (payment.amount * rate)?.toFixed(2);
          return {
            currency,
            symbol: getCurrencySymbol(currency),
            amount: convertedAmount
          };
        });

      return {
        primary: `${primarySymbol}${primaryAmount}`,
        secondary: secondaryAmounts
      };
    } else {
      // For sales/return transactions
      const salesTotal = payment.sales?.netTotal || 0;
      const primaryAmount = salesTotal.toFixed(2);

      // Generate secondary amounts for all other currencies
      const secondaryAmounts = availableCurrencies
        .filter(currency => currency !== primaryCurrency)
        .map(currency => {
          const rate = payment.currencyRates?.[currency] || 1;
          const convertedAmount = primaryCurrency === baseCurrency
            ? (salesTotal / rate)?.toFixed(2)
            : (salesTotal * rate)?.toFixed(2);
          return {
            currency,
            symbol: getCurrencySymbol(currency),
            amount: convertedAmount
          };
        });

      return {
        primary: `${primarySymbol}${primaryAmount}`,
        secondary: secondaryAmounts
      };
    }
  };

  // Helper function to get balance display
  const getBalanceDisplay = (payment) => {
    if (!payment.balances) return { primary: "0.00", secondary: [] };

    const primaryCurrency = payment.currency || baseCurrency;
    const primarySymbol = getCurrencySymbol(primaryCurrency);
    const primaryBalance = payment.balances[primaryCurrency]?.toFixed(2) || "0.00";

    // Generate secondary amounts for all other currencies
    const secondaryAmounts = availableCurrencies
      .filter(currency => currency !== primaryCurrency)
      .map(currency => {
        const balance = payment.balances[currency]?.toFixed(2) || "0.00";
        return {
          currency,
          symbol: getCurrencySymbol(currency),
          amount: balance
        };
      });

    return {
      primary: `${primarySymbol}${primaryBalance}`,
      secondary: secondaryAmounts
    };
  };

  // Helper to check if any balance exists
  const hasAnyBalance = (payment) => {
    if (!payment.balances) return false;
    return Object.values(payment.balances).some(balance => balance > 0);
  };

  return (
    <Fragment>
      {/* Back Button */}
      <div className="mb-3 tw-pt-6 d-flex justify-content-between">
        {/* page heading */}
        <h5 className="mb-0">{t("Patient Ledger Detailes")}</h5>
        <Button
          color="primary"
          size="sm"
          className="d-flex justify-content-center align-items-center"
          onClick={() => navigate(`${process.env.PUBLIC_URL}/patient-ledger`)}
        >
          <ArrowLeft size={16} className="me-1" />
          {t("Back to Ledger")}
        </Button>
      </div>

      {/* Patient Information Card */}
      {patientInfo && (
        <div className="my-4">
          <Card
            className={`${patientInfo?.is_death === false
              ? "patient-card-background"
              : "death-patient-card-background"
              } mb-0`}
          >
            <div className="p-2">
              <div className="p-2 d-flex tw-space-x-2">
                <S3ImageLoader
                  fileName={patientInfo.profile_picture}
                  alt={patientInfo.full_name}
                  className="patient-pic img-60"
                  fallbackSrc={errorProfile}
                />
                <div className="ps-3">
                  <h6 className="mb-0">
                    <strong>{patientInfo.full_name}</strong>
                  </h6>
                  <small
                    style={{
                      fontSize: "12px",
                      textTransform: "capitalize",
                    }}
                  >
                    {patientInfo.gender},{" "}
                    {moment(patientInfo.dob).format("DD MMM YYYY")} Age{" "}
                    {calculateAge(patientInfo.dob)}
                  </small>
                  <div className="patient-healthId-text tw-min-w-max">
                    {patientInfo.health_id?.match(/.{1,4}/g).join(" ")}
                  </div>
                </div>
                <div className="row ps-3">
                  <div className="col-md-6">
                    <div className="d-flex align-items-center">
                      <div className="p-0 rounded-circle me-2">
                        <FiPhoneCall />
                      </div>
                      <span style={{ fontSize: "12px" }}>
                        {patientInfo.phone_code} {patientInfo.mobile}
                      </span>
                    </div>
                  </div>
                  <div className="col-md-12">
                    <div className="d-flex align-items-center">
                      <div className="p-0 rounded-circle me-2">
                        <TfiEmail />
                      </div>
                      <span style={{ fontSize: "12px" }}>
                        {patientInfo.email}
                      </span>
                    </div>
                  </div>
                  <div className="col-md-12">
                    <div className="d-flex align-items-center">
                      <div className="p-0 rounded-circle me-2">
                        <FaLocationDot />
                      </div>
                      <small>{patientInfo.address}</small>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </Card>
        </div>
      )}

      {/* Summary Cards */}
      <div className="row">
        <div className="col-md-2">
          <Card className="text-white bg-primary">
            <CardBody className="text-center">
              <h4 className="mb-1">
                {Object.entries(ledgerData?.netTotal || {}).map(([currency, amount]) => (
                  <div key={currency} className={currency !== baseCurrency ? "fs-6" : ""}>
                    {getCurrencySymbol(currency)} {formatAmount(amount)}
                  </div>
                ))}
              </h4>
              <p className="mb-0">{t("Grand Total")}</p>
            </CardBody>
          </Card>
        </div>
        <div className="col-md-2">
          <Card className="text-white bg-success">
            <CardBody className="text-center">
              <h4 className="mb-1">
                {Object.entries(ledgerData?.totalPaidAmount || {}).map(([currency, amount]) => (
                  <div key={currency} className={currency !== baseCurrency ? "fs-6" : ""}>
                    {getCurrencySymbol(currency)} {formatAmount(amount)}
                  </div>
                ))}
              </h4>
              <p className="mb-0">{t("Total Paid")}</p>
            </CardBody>
          </Card>
        </div>
        <div className="col-md-2">
          <Card className="text-white bg-danger">
            <CardBody className="text-center">
              <h4 className="mb-1">
                {Object.entries(ledgerData?.totalDueAmount || {}).map(([currency, amount]) => (
                  <div key={currency} className={currency !== baseCurrency ? "fs-6" : ""}>
                    {getCurrencySymbol(currency)} {formatAmount(amount)}
                  </div>
                ))}
              </h4>
              <p className="mb-0">{t("Total Due")}</p>
            </CardBody>
          </Card>
        </div>
        <div className="col-md-2">
          <Card className="text-white bg-info">
            <CardBody className="text-center">
              <h4 className="mb-1">{ledgerData.paymentCount || 0}</h4>
              <p className="mb-0 mt-4">{t("Total Transactions")}</p>
            </CardBody>
          </Card>
        </div>
        <div className="col-md-2">
          <Card className="text-white bg-warning">
            <CardBody className="text-center">
              <h4 className="mb-1">
                {Object.entries(ledgerData?.totalRefund || {}).map(([currency, amount]) => (
                  <div key={currency} className={currency !== baseCurrency ? "fs-6" : ""}>
                    {getCurrencySymbol(currency)} {formatAmount(amount)}
                  </div>
                ))}
              </h4>
              <p className="mb-0">{t("Total Refund")}</p>
            </CardBody>
          </Card>
        </div>
        <div className="col-md-2">
          <Card className="text-white bg-secondary">
            <CardBody className="text-center">
              <h4 className="mb-1">
                {Object.entries(ledgerData?.totalReturn || {}).map(([currency, amount]) => (
                  <div key={currency} className={currency !== baseCurrency ? "fs-6" : ""}>
                    {getCurrencySymbol(currency)} {formatAmount(amount)}
                  </div>
                ))}
              </h4>
              <p className="mb-0">{t("Total Return")}</p>
            </CardBody>
          </Card>
        </div>
      </div>

      {/* Transactions Table */}
      <Card>
        <CardBody>
          <div className="mb-3 d-flex justify-content-between align-items-center">
            <h5 className="mb-0">
              {t("Transaction History")}</h5>
            <div className="text-muted small">
              {patientInfo ? t("Patient Name") : t("Customer Name")}: <strong className="uppercase">{ledgerData.payments[0]?.patientName}</strong>
              {/* <strong>Base Currency: {baseCurrency}</strong>
              {availableCurrencies.slice(1).map(currency => (
                <span key={currency} className="ms-2">{currency}</span>
              ))} */}
            </div>
          </div>
          <div className="table-responsive">
            <Table bordered hover className="align-middle ledger-table">
              <thead className="table-light">
                <tr className="fw-bold">
                  <th>#</th>
                  <th>{t("Invoice")}</th>
                  <th>{t("Date")}</th>
                  <th>{t("Transaction")}</th>

                  <th className="text-end">{t("Net Total")}</th>
                  <th className="text-end bg-success">{t("Credit (+)")}</th>
                  <th className="text-end bg-danger">{t("Debit (-)")}</th>

                  <th className="text-end">{t("Outstanding Balance")}</th>

                  <th>{t("Method")}</th>
                  <th>{t("Currency")}</th>

                  <th>{t("Status")}</th>
                  <th className="text-center">
                    {t("Action")}
                  </th>
                </tr>
              </thead>

              <tbody>
                {ledgerData.payments && ledgerData.payments.length > 0 ? (
                  ledgerData.payments.map((payment, idx) => {
                    const netTotalDisplay = getDisplayAmount(payment, false);
                    const isDebit = payment.salesType === "refund" ||
                      payment.salesType === "return";

                    const isCredit =
                      payment.salesType === "payment" ||
                      payment.salesType === "sales";

                    const creditDisplay = isCredit
                      ? getDisplayAmount(payment, true)
                      : { primary: "-", secondary: [] };

                    const debitDisplay = isDebit
                      ? getDisplayAmount(payment, true)
                      : { primary: "-", secondary: [] };

                    const balanceDisplay = getBalanceDisplay(payment);

                    return (
                      <tr key={payment._id}>
                        <td className="fw-bold text-muted">{idx + 1}</td>
                        <td className="fw-bold text-muted">{payment.salesNumber || '-'}</td>

                        <td>
                          <div className="fw-semibold">{formatDate(payment.createdAt)}</div>
                          {/* <small className="text-muted">
                            {payment.createdAt?.substring(11, 16)}
                          </small> */}
                        </td>

                        {/* Transaction Type Badge */}
                        <td>
                          <span
                            className={`badge px-3 py-2 rounded-pill 
                        ${payment.salesType === "sales"
                                ? "bg-primary"
                                : payment.salesType === "return"
                                  ? "bg-warning text-dark"
                                  : payment.salesType === "payment"
                                    ? "bg-success"
                                    : payment.salesType === "refund"
                                      ? "bg-danger"
                                      : "bg-secondary"
                              }
                      `}
                          >
                            {getTransactionTypeLabel(payment.salesType)}
                          </span>
                        </td>

                        {/* NET TOTAL */}
                        <td className="text-end fw-semibold">
                          {payment.salesType !== "payment" ? (
                            <span>{netTotalDisplay.primary}</span>
                            // {netTotalDisplay.secondary.length > 0 && netTotalDisplay.secondary.map((secondary, index) => (
                            //     <div key={index}>
                            //       <small className="text-muted">
                            //         {secondary.symbol}{secondary.amount}
                            //       </small>
                            //     </div>
                            //   ))}
                          ) : (
                            "-"
                          )}

                        </td>

                        {/* CREDIT */}
                        <td className="text-end fw-bold text-success">
                          {creditDisplay.primary}
                          {/* {creditDisplay.secondary.length > 0 && creditDisplay.secondary.map((secondary, index) => (
                                <div key={index}>
                                  <small className="text-muted">
                                    {secondary.symbol}{secondary.amount}
                                  </small>
                                </div>
                              ))} */}
                        </td>

                        {/* DEBIT */}
                        <td className="text-end fw-bold text-danger">
                          {debitDisplay.primary}
                          {/* {debitDisplay.secondary.length > 0 && debitDisplay.secondary.map((secondary, index) => (
                                <div key={index}>
                                  <small className="text-muted">
                                    {secondary.symbol}{secondary.amount}
                                  </small>
                                </div>
                              ))} */}
                        </td>

                        {/* BALANCE */}
                        <td className="text-end">
                          <span
                            className={`fw-bold ${hasAnyBalance(payment)
                              ? "text-danger"
                              : "text-success"
                              }`}
                          >
                            {balanceDisplay.primary}
                          </span>
                          {/* {balanceDisplay.secondary.length > 0 && balanceDisplay.secondary.map((secondary, index) => (
                              <div key={index}>
                                <small
                                  className={
                                    hasAnyBalance(payment)
                                      ? "text-danger"
                                      : "text-success"
                                  }
                                >
                                  {secondary.symbol}{secondary.amount}
                                </small>
                              </div>
                            ))} */}
                        </td>

                        {/* Payment Method */}
                        <td className="uppercase">
                          <span className="badge bg-light border text-dark">
                            {payment.paymentType}
                          </span>
                        </td>

                        {/* Currency */}
                        <td>
                          <strong>{payment.currency}</strong>
                          {payment.isConversion && (
                            <div>
                              <small className="badge rounded-pill bg-info text-dark mt-1">
                                {t("Converted")}
                              </small>
                            </div>
                          )}
                        </td>

                        {/* STATUS */}
                        <td>{getStatusBadge(payment)}</td>

                        {/* ACTION */}
                        <td className="text-center">
                          {payment.sales ? (
                            <>
                              <button
                                className="success-action-button me-1"
                                id={`ViewToolTip-${payment._id}`}
                                ref={(el) =>
                                  (eyeButtonRefs.current[payment._id] = el)
                                }
                                onClick={() => {
                                  if (eyeButtonRefs.current[payment._id]) {
                                    eyeButtonRefs.current[payment._id].blur();
                                  }
                                  setViewTooltipOpen((p) => ({ ...p, [payment._id]: false }));
                                  openDetailsModal(payment);
                                }}
                              >
                                <i className="fa fa-eye" style={{ fontSize: "18px" }}></i>
                              </button>
                              <ToolTip
                                attrToolTip={{
                                  placement: "top",
                                  isOpen: viewTooltipOpen[payment._id] || false,
                                  target: `ViewToolTip-${payment._id}`,
                                  toggle: () => toggleViewTooltip(payment._id),
                                }}
                              >
                                <strong>{t("View")}</strong>
                              </ToolTip>
                              {canShowPosReceipt(payment) && (
                                <>
                                  <button
                                    className="success-action-button me-1"
                                    id={`ReceiptToolTip-${payment._id}`}
                                    onClick={() => {
                                      setReceiptTooltipOpen((prev) => ({
                                        ...prev,
                                        [payment._id]: false,
                                      }));
                                      openPosReceipt(payment);
                                    }}
                                  >
                                    <i
                                      className="fa fa-print"
                                      style={{ fontSize: "18px" }}
                                    ></i>
                                  </button>
                                  <ToolTip
                                    attrToolTip={{
                                      placement: "top",
                                      isOpen: receiptTooltipOpen[payment._id] || false,
                                      target: `ReceiptToolTip-${payment._id}`,
                                      toggle: () => toggleReceiptTooltip(payment._id),
                                    }}
                                  >
                                    <strong>{t("POS Receipt")}</strong>
                                  </ToolTip>
                                </>
                              )}
                              {canViewPaymentInvoice(payment) && (
                                <>
                                  <button
                                    className="success-action-button"
                                    id={`InvoiceToolTip-${payment._id}`}
                                    onClick={() => {
                                      setInvoiceTooltipOpen((prev) => ({
                                        ...prev,
                                        [payment._id]: false,
                                      }));
                                      openInvoicePreview(payment);
                                    }}
                                  >
                                    <i
                                      className="fa fa-file-text-o"
                                      style={{ fontSize: "18px" }}
                                    ></i>
                                  </button>
                                  <ToolTip
                                    attrToolTip={{
                                      placement: "top",
                                      isOpen: invoiceTooltipOpen[payment._id] || false,
                                      target: `InvoiceToolTip-${payment._id}`,
                                      toggle: () => toggleInvoiceTooltip(payment._id),
                                    }}
                                  >
                                    <strong>{t("View Invoice")}</strong>
                                  </ToolTip>
                                </>
                              )}
                            </>
                          ) : (
                            '-'
                          )}
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan="11" className="p-4 text-center text-muted">
                      {t("No transactions found for this patient")}
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </div>
        </CardBody>
      </Card>

      <Modal
        show={showDetailsModal}
        onHide={() => setShowDetailsModal(false)}
        size="xl"
        centered
      >
        <Modal.Header
          className="gap-2 d-flex flex-column align-items-start"
          closeButton
        >
          <Modal.Title>{t("Transaction Details")}</Modal.Title>
          <div className="my-1 w-100 d-flex justify-content-between">
            <span>
              {t("Receipt Number")}:&nbsp;
              <strong>{selectedPaymentDetails?.receiptNumber}</strong>
            </span>
            <div className="d-flex gap-2">
              {canShowPosReceipt(selectedPaymentDetails) && (
                <Button variant="success" onClick={() => openPosReceipt(selectedPaymentDetails)}>
                  <i className="fa fa-receipt me-1"></i>
                  {t("POS Receipt")}
                </Button>
              )}
              <Button variant="primary" onClick={handlePrint}>
                <i className="fa fa-print me-2"></i>
                {t("Print")}
              </Button>
            </div>
          </div>
        </Modal.Header>

        <Card>
          <CardBody>
            {selectedPaymentDetails && (
              <div>
                <div className="align-baseline tw-mb-4 tw-w-full d-flex justify-content-between">
                  <div className="d-flex flex-column">
                    <h5>
                      {t("Patient Name")}:&nbsp;
                      <strong>{selectedPaymentDetails?.patientName}</strong>
                    </h5>
                    <span>
                      {t("Receipt Number")}:&nbsp;
                      <strong>{selectedPaymentDetails?.receiptNumber}</strong>
                    </span>
                    {selectedPaymentDetails?.sales?.salesNumber && (
                      <span>
                        {t("Invoice Number")}:&nbsp;
                        <strong>{selectedPaymentDetails?.sales?.salesNumber}</strong>
                      </span>
                    )}
                  </div>
                  <div className="text-end">
                    <span className="text-muted d-block">
                      {formatDate(selectedPaymentDetails?.createdAt)}
                    </span>
                    {selectedPaymentDetails?.sales?.salesType && (
                      <span className={`badge px-3 py-2 rounded-pill 
                  ${selectedPaymentDetails.sales.salesType === "sales"
                          ? "bg-primary"
                          : selectedPaymentDetails.sales.salesType === "return"
                            ? "bg-warning text-dark"
                            : selectedPaymentDetails.sales.salesType === "payment"
                              ? "bg-success"
                              : selectedPaymentDetails.sales.salesType === "refund"
                                ? "bg-danger"
                                : "bg-secondary"
                        }
                `}>
                        {getTransactionTypeLabel(selectedPaymentDetails.sales.salesType)}
                      </span>
                    )}
                  </div>
                </div>

                {/* Related Sales Information Tables */}
                {selectedPaymentDetails.sales &&
                  selectedPaymentDetails.sales.items?.length > 0 && (
                    <div className="mb-4">
                      <div key={selectedPaymentDetails.sales._id} className="mb-4">
                        <div className="table-responsive">
                          <table className="table table-sm table-bordered">
                            <thead className="table-light">
                              <tr>
                                <th>#</th>
                                <th>{t("Product Name")}</th>
                                <th>{t("Medicine Name")}</th>
                                <th>{t("Batch Number")}</th>
                                <th>{t("Product Form")}</th>
                                <th className="text-center">{t("Quantity")}</th>
                                <th className="text-end">{t("Unit Cost")}</th>
                                <th className="text-end">{t("Unit MRP")}</th>
                                {showSaleTax && (
                                  <>
                                    <th className="text-center">{t("Total Taxable Value")}</th>
                                    <th className="text-end">{t("Tax %")}</th>
                                    {selectedPaymentDetails.sales.isIGST === false ? (
                                      <>
                                        <th className="text-end">{t("CGST")}</th>
                                        <th className="text-end">{t("SGST")}</th>
                                      </>
                                    ) : (
                                      <th className="text-end">{t("IGST")}</th>
                                    )}
                                    <th className="text-end">{t("Total Tax")}</th>
                                  </>
                                )}
                                <th className="text-end">{t("Total")}</th>
                              </tr>
                            </thead>
                            <tbody>
                              {selectedPaymentDetails.sales.items.map(
                                (item, itemIndex) => {
                                  const itemPrice = Number(item.unitPricePerMedicine || item.sellingPrice || item.MRP || 0);
                                  const itemPriceTaxExcluded = itemPrice / (1 + (item.taxPercentage || selectedPaymentDetails.sales.taxPercentage || 0) / 100);
                                  const quantity = Number(item.medicineQunatity || 0);
                                  const grossTotal = itemPrice * quantity;
                                  const taxPercentage = item.taxPercentage || selectedPaymentDetails.sales.taxPercentage || 0;

                                  // Extraction formula
                                  const taxableAmount = grossTotal / (1 + taxPercentage / 100);
                                  const discountAmount = Number(item.discount) > 0 ? taxableAmount * (Number(item.discount) / 100) : 0;
                                  const netTaxableAmount = taxableAmount - discountAmount;
                                 
                                  const itemTax = netTaxableAmount * (taxPercentage / 100);

                                  // Determine tax split for item
                                  let CGST = 0, SGST = 0, IGST = 0;
                                  if (selectedPaymentDetails.sales.isIGST === false) {
                                    CGST = itemTax / 2;
                                    SGST = itemTax / 2;
                                  } else {
                                    IGST = itemTax;
                                  }

                                  return (
                                    <tr key={itemIndex}>
                                      <td>{itemIndex + 1}</td>
                                      <td>{item.brandName}</td>
                                      <td>{item.medicineName}</td>
                                      <td>{item.batchNumber}</td>
                                      <td>{item.productForm || "-"}</td>
                                      <td className="text-center">
                                        {quantity}
                                      </td>
                                      <td className="text-end">
                                        {getCurrencySymbol()}
                                        {item.unitPrice.toFixed(2)}
                                      </td>
                                      <td className="text-end">
                                        {getCurrencySymbol()}
                                        {itemPrice.toFixed(2)}
                                      </td>
                                      {showSaleTax && (
                                        <>
                                          <td className="text-end fw-bold">
                                            {getCurrencySymbol()}
                                            {netTaxableAmount.toFixed(2)}
                                          </td>
                                          <td className="text-end">
                                            {taxPercentage}%
                                          </td>
                                          {selectedPaymentDetails.sales.isIGST === false ? (
                                            <>
                                              <td className="text-end text-success">
                                                {getCurrencySymbol()}
                                                {CGST.toFixed(2)}
                                              </td>
                                              <td className="text-end text-success">
                                                {getCurrencySymbol()}
                                                {SGST.toFixed(2)}
                                              </td>
                                            </>
                                          ) : (
                                            <td className="text-end text-success">
                                              {getCurrencySymbol()}
                                              {IGST.toFixed(2)}
                                            </td>
                                          )}
                                          <td className="text-end text-success">
                                            {getCurrencySymbol()}
                                            {itemTax.toFixed(2)}
                                          </td>
                                        </>
                                      )}
                                      <td className="text-end fw-bold">
                                        {getCurrencySymbol()}
                                        {grossTotal.toFixed(2)}
                                      </td>
                                    </tr>
                                  );
                                }
                              )}
                            </tbody>
                            <tfoot className="table-secondary">
                              <tr>
                                <td colSpan={showSaleTax ? 9 : 8} className="text-end fw-bold">
                                  {showSaleTax ? t("Total Taxable Value") : t("Subtotal")}:
                                </td>
                                {showSaleTax && (
                                  <>
                                    <td className="text-end fw-bold text-muted">
                                      {hasMixedTaxSlabs
                                        ? t("Mixed GST")
                                        : `${selectedPaymentDetails.sales.taxPercentage || "0"}%`}
                                    </td>
                                    {selectedPaymentDetails.sales.isIGST === false ? (
                                      <>
                                        <td className="text-end text-success fw-bold">
                                          {getCurrencySymbol()}
                                          {calculatedSalesFooter?.CGST?.toFixed(2) || "0.00"}
                                        </td>
                                        <td className="text-end text-success fw-bold">
                                          {getCurrencySymbol()}
                                          {calculatedSalesFooter?.SGST?.toFixed(2) || "0.00"}
                                        </td>
                                      </>
                                    ) : (
                                      <td className="text-end text-success fw-bold">
                                        {getCurrencySymbol()}
                                        {calculatedSalesFooter?.IGST?.toFixed(2) || "0.00"}
                                      </td>
                                    )}
                                    <td className="text-end text-success fw-bold">
                                      {getCurrencySymbol()}
                                      {calculatedSalesFooter?.totalTax?.toFixed(2) || "0.00"}
                                    </td>
                                  </>
                                )}
                                <td className="text-end fw-bold">
                                  {getCurrencySymbol()}
                                  {calculatedSalesFooter?.totalPrice?.toFixed(2) || "0.00"}
                                </td>
                              </tr>
                            </tfoot>
                          </table>
                        </div>
                      </div>
                    </div>
                  )}



                <div className="row p-3 border rounded">
                  {/* Tax Breakdown Summary */}
                  <div className="col-12 mb-sm-2 col-md-6">
                    {showSaleTax && selectedPaymentDetails.sales?.tax > 0 && (
                      <div className="mb-4">
                        <h6 className="mb-3">{t("Tax Breakdown")}</h6>
                        <table className="table table-bordered table-sm">
                          <thead className="table-light">
                            <tr>
                              <th>{t("Tax Type")}</th>
                              <th className="text-end">{t("Rate")}</th>
                              <th className="text-end">{t("Amount")}</th>
                            </tr>
                          </thead>
                          <tbody>
                            {selectedPaymentDetails.sales.isIGST === false ? (
                              <>
                                {hasMixedTaxSlabs ? (
                                  taxBreakdownBySlab.map(([slab, values]) => (
                                    <tr key={slab}>
                                      <td>{t("GST")} @{slab}%</td>
                                      <td className="text-end">—</td>
                                      <td className="text-end text-success">
                                        {getCurrencySymbol()}
                                        {(values.CGST + values.SGST + values.IGST).toFixed(2)}
                                      </td>
                                    </tr>
                                  ))
                                ) : (
                                  <>
                                    <tr>
                                      <td>{t("CGST")}</td>
                                      <td className="text-end">
                                        {(selectedPaymentDetails.sales.CGSTPercentage || taxSlabs[0] / 2).toFixed(2)}%
                                      </td>
                                      <td className="text-end text-success">
                                        {getCurrencySymbol()}
                                        {(selectedPaymentDetails.sales.CGST ?? calculatedSalesFooter?.CGST ?? 0).toFixed(2)}
                                      </td>
                                    </tr>
                                    <tr>
                                      <td>{t("SGST")}</td>
                                      <td className="text-end">
                                        {(selectedPaymentDetails.sales.SGSTPercentage || taxSlabs[0] / 2).toFixed(2)}%
                                      </td>
                                      <td className="text-end text-success">
                                        {getCurrencySymbol()}
                                        {(selectedPaymentDetails.sales.SGST ?? calculatedSalesFooter?.SGST ?? 0).toFixed(2)}
                                      </td>
                                    </tr>
                                  </>
                                )}
                              </>
                            ) : (
                              <tr>
                                <td>{t("IGST")}</td>
                                <td className="text-end">
                                  {/* If mixed, showing percentage might be ambiguous, keeping safe default */}
                                  {hasMixedTaxSlabs ? "Mixed" : `${taxSlabs[0]?.toFixed(2)}%`}
                                </td>
                                <td className="text-end text-success">
                                  {getCurrencySymbol()}
                                  {calculatedSalesFooter?.IGST?.toFixed(2) || "0.00"}
                                </td>
                              </tr>
                            )}
                            <tr className="table-warning">
                              <td colSpan="2" className="fw-bold">
                                {t("Total Tax")}
                                {hasMixedTaxSlabs && (
                                  <small className="text-muted ms-2">
                                    ({t("Item-wise GST applied")})
                                  </small>
                                )}
                              </td>
                              <td className="text-end fw-bold text-success">
                                {getCurrencySymbol()}
                                {selectedPaymentDetails.sales.tax?.toFixed(2) || "0.00"}
                              </td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                  <div className="col-12 mb-sm-2 col-md-6">
                    {/* Transaction Summary */}
                    <div className="mb-4">
                      <h6 className="mb-3">{t("Transaction Summary")}</h6>
                      <table className="table mb-0 table-sm">
                        <tbody>
                          <tr>
                            <td className="border-0">
                              {showSaleTax ? t("Total Taxable Value") : t("Subtotal")}
                            </td>
                            <td className="border-0 text-end">
                              {getCurrencySymbol()}
                              {(showSaleTax
                                ? calculatedSalesFooter?.subtotal
                                : calculatedSalesFooter?.totalPrice
                              )?.toFixed(2) || "0.00"}
                            </td>
                          </tr>

                          {selectedPaymentDetails.sales?.discount > 0 && (
                            <>
                              <tr>
                                <td className="border-0">
                                  {t("Discount")} ({selectedPaymentDetails.sales.discountType === "percentage" ? `${selectedPaymentDetails.sales.discount}%` : `${selectedPaymentDetails.sales.discountAmount}`})
                                </td>
                                <td className="border-0 text-end text-danger">
                                  -{getCurrencySymbol()}
                                  {calculatedSalesFooter?.discountAmt?.toFixed(2) || "0.00"}
                                </td>
                              </tr>
                              <tr>
                                <td className="border-0 fw-semibold">
                                  {showSaleTax ? t("Net Taxable Value") : t("Net Amount")}
                                </td>
                                <td className="border-0 text-end fw-semibold">
                                  {getCurrencySymbol()}
                                  {(
                                    (showSaleTax
                                      ? calculatedSalesFooter?.subtotal || 0
                                      : calculatedSalesFooter?.totalPrice || 0) -
                                    (calculatedSalesFooter?.discountAmt || 0)
                                  ).toFixed(2)}
                                </td>
                              </tr>
                            </>
                          )}

                          {selectedPaymentDetails.sales?.transportCost > 0 && (
                            <tr>
                              <td className="border-0">{t("Shipping")}</td>
                              <td className="border-0 text-end">
                                {getCurrencySymbol()}
                                {selectedPaymentDetails.sales.transportCost.toFixed(2)}
                              </td>
                            </tr>
                          )}

                          {showSaleTax && selectedPaymentDetails.sales?.tax > 0 && (
                            <tr className="table-light">
                              <td className="border-0">
                                {selectedPaymentDetails.sales.isIGST ? "IGST" : "GST"}
                                <small className="text-muted ms-1">
                                  {hasMixedTaxSlabs
                                    ? t("(Item-wise GST applied)")
                                    : `(${selectedPaymentDetails.sales.taxPercentage}%)`}
                                </small>
                              </td>
                              <td className="border-0 text-end text-success">
                                +{getCurrencySymbol()}
                                {selectedPaymentDetails.sales.tax.toFixed(2)}
                              </td>
                            </tr>
                          )}

                          <tr className="table-primary">
                            <td className="border-0 fw-bold">
                              {t("Grand Total")}
                            </td>
                            <td className="border-0 text-end fw-bold">
                              <div className="d-flex flex-column">
                                {currency !== selectedPaymentDetails.currency && (
                                  <span className="tw-text-[10px] text-muted">
                                    {getCurrencySymbol()}
                                    {convertCurrency(
                                      selectedPaymentDetails.sales?.netTotal || selectedPaymentDetails.amount || 0,
                                      selectedPaymentDetails.currency,
                                      currency
                                    ).toFixed(2)}
                                  </span>
                                )}
                                <strong>
                                  {getCurrencySymbol(selectedPaymentDetails.currency)}
                                  {(selectedPaymentDetails.sales?.netTotal ?? selectedPaymentDetails.amount ?? 0).toFixed(2)}
                                </strong>
                              </div>
                            </td>
                          </tr>


                          <tr>
                            <td className="border-0">
                              {t("Amount Paid")}
                            </td>
                            <td className="border-0 text-end text-success">
                              <strong>
                                {getCurrencySymbol(selectedPaymentDetails.currency)}
                                {selectedPaymentDetails.amount?.toFixed(2) || "0.00"}
                              </strong>
                            </td>
                          </tr>

                          {selectedPaymentDetails.balances && hasAnyBalance(selectedPaymentDetails) && (
                            <tr className="table-warning">
                              <td className="fw-bold">{t("Outstanding Balance")}</td>
                              <td className="text-end">
                                <strong className="text-danger">
                                  {getCurrencySymbol(selectedPaymentDetails.currency)}
                                  {selectedPaymentDetails.balances[selectedPaymentDetails.currency]?.toFixed(2) || "0.00"}
                                </strong>
                              </td>
                            </tr>
                          )}

                          {!hasAnyBalance(selectedPaymentDetails) && selectedPaymentDetails.balances && (
                            <tr className="table-success">
                              <td className="fw-bold">{t("Payment Status")}</td>
                              <td className="text-end">
                                <strong className="text-success">
                                  {t("Fully Paid")}
                                </strong>
                              </td>
                            </tr>
                          )}

                          {/* Payment Method */}
                          <tr>
                            <td className="border-0">{t("Payment Method")}</td>
                            <td className="border-0 text-end">
                              <span className="badge bg-light text-dark">
                                {selectedPaymentDetails.paymentType?.toUpperCase()}
                              </span>
                              {selectedPaymentDetails.paymentType === "cash" && (
                                <IoCashOutline className="ms-2" />
                              )}
                              {selectedPaymentDetails.paymentType === "card" && (
                                <FaRegCreditCard className="ms-2" />
                              )}
                              {selectedPaymentDetails.paymentType === "online" && (
                                <FaMoneyBillWave className="ms-2" />
                              )}
                            </td>
                          </tr>

                          {/* Currency Info */}
                          {selectedPaymentDetails.isConversion && (
                            <tr>
                              <td className="border-0">{t("Currency Conversion")}</td>
                              <td className="border-0 text-end">
                                <small className="badge rounded-pill bg-info text-dark">
                                  {t("Converted from")} {selectedPaymentDetails.baseCurrency}
                                </small>
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
                {/* Additional Information */}
                {/* <div className="mt-4 p-3 border rounded bg-light">
                  <h6 className="mb-3">{t("Additional Information")}</h6>
                  <div className="row">
                    <div className="col-md-6">
                      <div className="mb-2">
                        <small className="text-muted">{t("Transaction ID")}</small>
                        <div>{selectedPaymentDetails._id}</div>
                      </div>
                      {selectedPaymentDetails.sales?.salesId && (
                        <div className="mb-2">
                          <small className="text-muted">{t("Sale ID")}</small>
                          <div>{selectedPaymentDetails.sales.salesId}</div>
                        </div>
                      )}
                    </div>
                    <div className="col-md-6">
                      <div className="mb-2">
                        <small className="text-muted">{t("Transaction Date")}</small>
                        <div>{formatDate(selectedPaymentDetails.createdAt)}</div>
                      </div>
                      <div className="mb-2">
                        <small className="text-muted">{t("Tax System")}</small>
                        <div>
                          {selectedPaymentDetails.sales?.isIGST ? "IGST" : "GST (CGST + SGST)"}
                        </div>
                      </div>
                    </div>
                  </div>
                </div> */}
              </div>
            )}
          </CardBody>
        </Card>

        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowDetailsModal(false)}
          >
            {t("Close")}
          </Button>
        </Modal.Footer>
      </Modal>

      <InvoicePreviewModal
        isOpen={invoicePreviewOpen}
        toggle={closeInvoicePreview}
        invoiceUrl={previewInvoiceUrl}
        invoiceNo={previewInvoiceNo}
      />

      <InvoiceModal
        t={t}
        show={showPosInvoice}
        invoiceData={posInvoiceData}
        pharmacyData={pharmacyPOS}
        onHide={closePosReceipt}
        onClose={closePosReceipt}
        onPrint={printPosInvoice}
      />
    </Fragment>
  );
};

export default ViewPatientLedger;